import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../hooks/useAuth";
import { getForgotPasswordErrorMessage } from "../utils/authErrors";
import "./CSS/auth.css";
import cael from "./CSS/cael.png";

const ForgotPassword = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { forgotPassword, isSendingResetEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await forgotPassword(email);
      if (result.status === "success") {
        setSubmitted(true);
      } else {
        setError(
          getForgotPasswordErrorMessage(
            { message: result.message, status: result.status },
            t
          )
        );
      }
    } catch (err) {
      setError(getForgotPasswordErrorMessage(err, t));
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg"></div>

      <div className="auth-card">
        <Link to="/" className="auth-logo-link">
          <img className="auth-logo" src={cael} alt="St Cael Vog" />
        </Link>

        {!submitted ? (
          <>
            <div className="auth-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>

            <h2>{t('forgotPassword.title')}</h2>
            <p className="auth-description">
              {t('forgotPassword.subtitle')}
            </p>

            {error && <div className="auth-error">{error}</div>}

            <form className="auth-form" onSubmit={handleSubmit}>
              <input
                type="email"
                className="auth-input"
                placeholder={t('forgotPassword.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                required
              />
              <button
                type="submit"
                className="auth-submit-btn"
                disabled={isSendingResetEmail}
              >
                {isSendingResetEmail ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
              </button>
            </form>
          </>
        ) : (
          <div className="auth-success">
            <div className="auth-success-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h2>{t('forgotPassword.success.title')}</h2>
            <p className="auth-success-text">
              {t('forgotPassword.success.message')}
              <br />
              <strong>{email}</strong>
            </p>
            <p className="auth-hint">
              {t('forgotPassword.success.checkEmail', { defaultValue: "Didn't receive the email? Check your spam folder or" })}
              <button
                type="button"
                className="auth-resend-link"
                onClick={() => setSubmitted(false)}
              >
                {t('forgotPassword.tryAgain', { defaultValue: 'try again' })}
              </button>
            </p>
            <div className="auth-form-submit-group auth-success-actions">
              <button
                type="button"
                className="auth-submit-btn"
                onClick={() => navigate('/reset-password', { state: { email } })}
              >
                {t('forgotPassword.success.enterCode')}
              </button>
            </div>
          </div>
        )}

        <Link to="/login" className="auth-back-link">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          {t('forgotPassword.success.backToLogin')}
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
